/* eslint-disable better-mutation/no-mutation */

import dayjs from 'dayjs';
import pick from 'lodash/pick';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
    ItemAdditionalDescription,
    ItemDraftMetadata
} from 'tradera/state/services/types/webapi-selling-generated';

type DraftReason =
    | 'NewItem'
    | 'Template'
    | 'ChangeItem'
    | 'ChangeItemWithBids'
    | 'CopyOtherMembersItem'
    | 'CopyOwnItem'
    | 'RestartItem'
    | 'ResellItem'
    | 'SellSimilarItem';

export type DraftSlice = {
    isLoading: boolean;
    draftId?: string;
    draftReason?: DraftReason;
    memberId?: number;
    itemId?: number;
    restart?: boolean;
    publishedPromoted?: boolean;
    publishedHighlighted?: boolean;
    createdDate?: string;
    lastModifiedDate?: string;
    publishedDate?: unknown;
    publishedAsItemType?: unknown;
    reference?: string | null;
    referenceIsReadonly?: boolean;
    additionalDescriptions: ItemAdditionalDescription[];
    metadata?: ItemDraftMetadata;
    templateId?: string;
    templateName?: string;
    version?: number;
    isNewDraft?: unknown;
    descriptionLanguageCodeIso2?: string;
    currency?: string;
    resellPartner?: string;
    magicalSyiEnabled?: boolean;
};

const initialState: DraftSlice = {
    isLoading: false,
    additionalDescriptions: []
};

const draftSlice = createSlice({
    name: 'draft',
    initialState,
    reducers: {
        setDraftProperties(state, action: PayloadAction<Partial<DraftSlice>>) {
            const draft = action.payload;
            const referenceIsReadonly =
                draft.reference?.includes('Sello artnr') ?? false;
            if (JSON.stringify(state) != JSON.stringify(draft)) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            Object.assign(
                state,
                pick(draft, [
                    'draftId',
                    'draftReason',
                    'memberId',
                    'itemId',
                    'restart',
                    'publishedPromoted',
                    'publishedHighlighted',
                    'createdDate',
                    'lastModifiedDate',
                    'publishedDate',
                    'publishedAsItemType',
                    'reference',
                    'additionalDescriptions',
                    'metadata',
                    'templateId',
                    'templateName',
                    'version'
                ]),
                {
                    referenceIsReadonly,
                    isNewDraft: !draft.draftId
                }
            );
        },
        setDraftId(state, action: PayloadAction<string>) {
            if (state.draftId !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.draftId = action.payload;
        },
        setRestart(state, action: PayloadAction<boolean>) {
            if (state.restart !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.restart = action.payload;
        },
        disableRestart(state) {
            if (state.restart !== false) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.restart = false;
        },
        setReference(state, action: PayloadAction<string | null>) {
            if (state.reference !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.reference = action.payload;
        },
        addAdditionalDescription(state, action: PayloadAction<string>) {
            const newAdditionalDescriptions = [
                ...state.additionalDescriptions,
                { message: action.payload } as ItemAdditionalDescription
            ];
            if (
                JSON.stringify(state.additionalDescriptions) !=
                JSON.stringify(newAdditionalDescriptions)
            ) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.additionalDescriptions = newAdditionalDescriptions;
        },
        setDraftReason(state, action: PayloadAction<DraftReason>) {
            if (state.draftReason !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.draftReason = action.payload;
        },
        ensureDraftReason(state) {
            if (!state.draftReason) {
                state.draftReason = 'NewItem';
            }
        },
        setDraftVersion(state, action: PayloadAction<number>) {
            if (state.version !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.version = action.payload;
        },
        resetDraft() {
            return initialState;
        },
        setDraftIsLoading(state, action: PayloadAction<boolean>) {
            if (state.isLoading !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.isLoading = action.payload;
        },
        setCurrency(state, action: PayloadAction<string>) {
            if (state.currency !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.currency = action.payload;
        },
        setResellPartner(state, action: PayloadAction<string>) {
            if (state.resellPartner !== action.payload) {
                state.lastModifiedDate = dayjs().toISOString();
            }
            state.resellPartner = action.payload;
        },
        setMagicalSyiEnabled(state, action: PayloadAction<boolean>) {
            state.magicalSyiEnabled = action.payload;
        }
    }
});

export const {
    setDraftProperties,
    setDraftId,
    setRestart,
    disableRestart,
    setReference,
    addAdditionalDescription,
    setDraftReason,
    ensureDraftReason,
    setDraftVersion,
    resetDraft,
    setDraftIsLoading,
    setCurrency,
    setResellPartner,
    setMagicalSyiEnabled
} = draftSlice.actions;

export default draftSlice.reducer;
